import React, { useState, useEffect, useMemo } from "react";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import {
  Plus, CheckCircle2, Circle, Clock, Pencil, Trash2,
  Play, Square, User, Hourglass, Percent, X,
  ChevronDown, ChevronUp, Calendar, RefreshCw,
  Trophy, BookOpen, Zap, Users, Pause, RotateCcw,
} from "lucide-react";
import { Badge } from "components/ui/badge";
import { format } from "date-fns";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "components/ui/alert-dialog";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return h + "h " + m + "m " + s + "s";
  if (m > 0) return m + "m " + s + "s";
  return s + "s";
}

function formatMinutes(mins) {
  if (mins === null || mins === undefined) return "—";
  if (mins < 60) return `${Math.round(mins)}m`;
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}

const priorityColors = {
  Dringend: "text-red-600",
  Hoch: "text-orange-600",
  Normal: "text-slate-600",
  Niedrig: "text-slate-400",
};

const priorityBorder = {
  Dringend: "border-l-red-500",
  Hoch: "border-l-orange-400",
  Normal: "border-l-slate-300",
  Niedrig: "border-l-slate-200",
};

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------

function LiveTimer({ startedAt, accumulatedSeconds = 0 }) {
  const [elapsed, setElapsed] = useState(accumulatedSeconds);
  useEffect(() => {
    if (!startedAt) {
      setElapsed(accumulatedSeconds);
      return;
    }
    const update = () => {
      const sessionSecs = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
      setElapsed(accumulatedSeconds + sessionSecs);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startedAt, accumulatedSeconds]);
  return <span className="font-mono text-sm text-emerald-700">{formatDuration(elapsed)}</span>;
}

function LiveCost({ startedAt, hourlyRate, accumulatedSeconds = 0 }) {
  const [cost, setCost] = useState(0);
  useEffect(() => {
    if (hourlyRate <= 0) return;
    if (!startedAt) {
      setCost((accumulatedSeconds / 3600) * hourlyRate);
      return;
    }
    const update = () => {
      const sessionHours = (Date.now() - new Date(startedAt).getTime()) / 3600000;
      const totalHours = (accumulatedSeconds / 3600) + sessionHours;
      setCost(totalHours * hourlyRate);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startedAt, hourlyRate, accumulatedSeconds]);
  return <span className="font-mono text-sm text-emerald-600">€{cost.toFixed(2)}</span>;
}

function TaskActionModal({ task, open, onClose, onComplete, onPause, onReset }) {
  if (!task) return null;
  const isPaused = task.paused_at && !task.started_at;
  const accSecs = task.accumulated_seconds || 0;
  const hourlyRate = parseFloat(task.assigned_user_hourly_rate) || parseFloat(task.employee_hourly_rate) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">{task.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5 pt-2">
          {/* Complete */}
          <button
            onClick={() => { onComplete(task); onClose(); }}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all text-left group"
          >
            <div className="p-2 bg-emerald-600 rounded-lg text-white group-hover:bg-emerald-700 transition-colors">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-emerald-900">Complete</p>
              <p className="text-xs text-emerald-600">Mark as done and calculate cost</p>
            </div>
          </button>

          {/* Pause (only if currently running, not already paused) */}
          {!isPaused && (
            <button
              onClick={() => { onPause(task); onClose(); }}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left group"
            >
              <div className="p-2 bg-amber-500 rounded-lg text-white group-hover:bg-amber-600 transition-colors">
                <Pause className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">Pause</p>
                <p className="text-xs text-amber-600">Save progress, continue later</p>
              </div>
            </button>
          )}

          {/* Reset */}
          <button
            onClick={() => { onReset(task); onClose(); }}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-slate-200 bg-slate-50 hover:bg-red-50 hover:border-red-200 transition-all text-left group"
          >
            <div className="p-2 bg-slate-400 rounded-lg text-white group-hover:bg-red-500 transition-colors">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-slate-700 group-hover:text-red-800">Reset</p>
              <p className="text-xs text-slate-500 group-hover:text-red-600">Discard all progress, start fresh</p>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RankBadge({ rank }) {
  const styles = [
    "bg-yellow-100 text-yellow-800 border border-yellow-300",
    "bg-slate-100 text-slate-600 border border-slate-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
  ];
  const cls = styles[rank - 1] || "bg-slate-50 text-slate-400 border border-slate-100";
  return <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${cls}`}>#{rank}</span>;
}

// ---------------------------------------------------------------------------
// LIBRARY TAB — Task templates
// ---------------------------------------------------------------------------

function LibraryTab({ templates, instanceMap, onEdit, onDelete, onAssign, onStartTimer, onOpenAction }) {
  if (templates.length === 0) {
    return (
      <div className="text-center py-16">
        <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
        <p className="text-slate-500 mb-1">No tasks yet</p>
        <p className="text-xs text-slate-400">Click "Create Task" to build your first task template</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {templates.map((task) => {
        const instances = instanceMap[task.id] || { active: [], completed: [] };
        return (
          <TemplateCard
            key={task.id}
            task={task}
            instances={instances}
            onEdit={onEdit}
            onDelete={onDelete}
            onAssign={onAssign}
            onStartTimer={onStartTimer}
            onOpenAction={onOpenAction}
          />
        );
      })}
    </div>
  );
}

function TemplateCard({ task, instances, onEdit, onDelete, onAssign, onStartTimer, onOpenAction }) {
  const [showCompleted, setShowCompleted] = useState(false);
  const { active, completed } = instances;

  return (
    <div className={`border border-slate-200 border-l-4 ${priorityBorder[task.priority] || "border-l-slate-200"} rounded-xl overflow-hidden hover:shadow-md transition-all bg-white`}>
      {/* Template header */}
      <div className="flex items-center gap-4 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <p className="font-semibold text-slate-900">{task.title}</p>
            {task.recurring && (
              <RefreshCw className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="outline" className="text-xs">{task.priority}</Badge>
            <Badge variant="outline" className="text-xs text-slate-400">{task.category}</Badge>
            {active.length > 0 && (
              <span className="text-xs text-orange-600 font-medium">{active.length} active</span>
            )}
            {completed.length > 0 && (
              <span className="text-xs text-emerald-600">{completed.length} done</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 h-8 text-xs gap-1.5" onClick={() => onAssign(task)}>
            <User className="w-3.5 h-3.5" /> Assign
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => onEdit(task)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(task.id)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Active instances */}
      {active.length > 0 && (
        <div className="border-t border-slate-100 bg-orange-50/30 px-4 py-2.5 space-y-1.5">
          {active.map((t) => {
            const isRunning = t.started_at && !t.completed;
            const isPaused = !t.started_at && t.paused_at && !t.completed;
            const accSecs = t.accumulated_seconds || 0;
            return (
              <div key={t.id} className={`flex items-center gap-3 text-sm py-2 px-3 rounded-lg bg-white border ${isRunning ? "border-emerald-200" : isPaused ? "border-amber-200" : "border-slate-100"}`}>
                {isRunning ? (
                  <Hourglass className="w-4 h-4 text-emerald-600 flex-shrink-0 animate-pulse" />
                ) : isPaused ? (
                  <Pause className="w-4 h-4 text-amber-500 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                )}
                <span className="flex-1 truncate font-medium text-slate-700">
                  {t.assigned_user_display_name || t.assigned_to || "Unassigned"}
                </span>
                <span className="text-xs text-slate-400">{format(new Date(t.due_date), "dd.MM")}</span>
                {isRunning && <LiveTimer startedAt={t.started_at} accumulatedSeconds={accSecs} />}
                {isPaused && <span className="font-mono text-xs text-amber-600">{formatDuration(accSecs)}</span>}
                {isRunning ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => onOpenAction(t)}>
                    <Square className="w-3 h-3" /> Stop
                  </Button>
                ) : isPaused ? (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => onStartTimer(t)}>
                      <Play className="w-3 h-3" /> Resume
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-600" onClick={() => onOpenAction(t)}>
                      <Square className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => onStartTimer(t)}>
                    <Play className="w-3 h-3" /> Start
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Completed instances — collapsible */}
      {completed.length > 0 && (
        <div className="border-t border-slate-100">
          <button
            onClick={() => setShowCompleted((v) => !v)}
            className="w-full flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-emerald-600 hover:bg-emerald-50/50 transition-colors"
          >
            <CheckCircle2 className="w-3 h-3" />
            {completed.length} Completed
            {showCompleted ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
          </button>
          {showCompleted && (
            <div className="px-4 pb-2.5 space-y-1">
              {completed.map((t) => {
                const duration = t.accumulated_seconds
                  ? formatDuration(t.accumulated_seconds)
                  : (t.started_at && t.completed_at)
                    ? formatDuration(Math.floor((new Date(t.completed_at) - new Date(t.started_at)) / 1000))
                    : null;
                return (
                  <div key={t.id} className="flex items-center gap-3 text-sm py-1.5 px-3 rounded-lg bg-slate-50">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="flex-1 truncate text-slate-400">{t.assigned_user_display_name || t.assigned_to}</span>
                    {duration && <span className="text-xs text-slate-400"><Clock className="w-3 h-3 inline mr-0.5" />{duration}</span>}
                    {t.task_cost != null && <span className="text-xs text-emerald-600 font-medium">€{parseFloat(t.task_cost).toFixed(2)}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TEAM TAB — Employee-grouped board
// ---------------------------------------------------------------------------

function TeamTab({ tasks, users, isEmployee, authUser, onStartTimer, onOpenAction, onDelete }) {
  const [dateFilter, setDateFilter] = useState("all");
  const today = format(new Date(), "yyyy-MM-dd");

  // Build employee → tasks map
  const employeeMap = useMemo(() => {
    const map = {};
    const nonTemplateTasks = tasks.filter((t) => !t.is_template);

    // Filter by date
    let filtered = nonTemplateTasks;
    if (dateFilter === "today") {
      filtered = nonTemplateTasks.filter((t) => t.due_date === today);
    } else if (dateFilter === "week") {
      const weekLater = new Date();
      weekLater.setDate(weekLater.getDate() + 7);
      const weekEnd = format(weekLater, "yyyy-MM-dd");
      filtered = nonTemplateTasks.filter((t) => t.due_date >= today && t.due_date <= weekEnd);
    }

    // If employee, only show their tasks
    if (isEmployee) {
      filtered = filtered.filter((t) => t.assigned_user_id === authUser?.id);
    }

    for (const t of filtered) {
      const empKey = t.assigned_user_id || t.assigned_to || "unassigned";
      const empName = t.assigned_user_display_name || t.assigned_to || "Unassigned";
      if (!map[empKey]) {
        map[empKey] = { key: empKey, name: empName, active: [], completed: [] };
      }
      if (t.completed) {
        map[empKey].completed.push(t);
      } else {
        map[empKey].active.push(t);
      }
    }

    // Sort: employees with active tasks first, then by name
    return Object.values(map).sort((a, b) => {
      if (a.active.length > 0 && b.active.length === 0) return -1;
      if (a.active.length === 0 && b.active.length > 0) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [tasks, dateFilter, today, isEmployee, authUser]);

  const totalActive = employeeMap.reduce((s, e) => s + e.active.length, 0);
  const totalCompleted = employeeMap.reduce((s, e) => s + e.completed.length, 0);

  return (
    <div className="space-y-4">
      {/* Date filter bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { key: "today", label: "Today" },
            { key: "week", label: "This Week" },
            { key: "all", label: "All" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setDateFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                dateFilter === f.key
                  ? "bg-emerald-700 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Circle className="w-3 h-3 text-orange-400" />{totalActive} active</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" />{totalCompleted} done</span>
        </div>
      </div>

      {employeeMap.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No tasks {dateFilter === "today" ? "due today" : dateFilter === "week" ? "this week" : "assigned"}</p>
        </div>
      ) : (
        employeeMap.map((emp) => (
          <EmployeeSection
            key={emp.key}
            employee={emp}
            isEmployee={isEmployee}
            onStartTimer={onStartTimer}
            onOpenAction={onOpenAction}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  );
}

function EmployeeSection({ employee, isEmployee, onStartTimer, onOpenAction, onDelete }) {
  const [showCompleted, setShowCompleted] = useState(false);
  const { active, completed, name } = employee;
  const hasRunning = active.some((t) => t.started_at && !t.completed);
  const hasPaused = active.some((t) => !t.started_at && t.paused_at && !t.completed);

  return (
    <Card className={`border-0 shadow-sm overflow-hidden ${hasRunning ? "ring-2 ring-emerald-200" : hasPaused ? "ring-2 ring-amber-200" : ""}`}>
      {/* Employee header */}
      <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${hasRunning ? "bg-emerald-600" : hasPaused ? "bg-amber-500" : "bg-slate-400"}`}>
          {name.charAt(0).toUpperCase()}
        </div>
        <span className="font-semibold text-slate-800 flex-1">{name}</span>
        <span className="text-xs text-slate-500">{active.length} active</span>
        {completed.length > 0 && (
          <button
            onClick={() => setShowCompleted((v) => !v)}
            className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            <CheckCircle2 className="w-3 h-3" />
            {completed.length} done
            {showCompleted ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      <CardContent className="p-0">
        {/* Active tasks */}
        {active.length === 0 && !showCompleted && (
          <p className="text-sm text-slate-400 px-5 py-4">No active tasks</p>
        )}
        {active.map((task) => {
          const isRunning = task.started_at && !task.completed;
          const isPaused = !task.started_at && task.paused_at && !task.completed;
          const accSecs = task.accumulated_seconds || 0;
          const hourlyRate = parseFloat(task.assigned_user_hourly_rate) || parseFloat(task.employee_hourly_rate) || 0;

          return (
            <div
              key={task.id}
              className={`flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-0 ${
                isRunning ? "bg-emerald-50/50" : isPaused ? "bg-amber-50/30" : "hover:bg-slate-50"
              } transition-colors`}
            >
              {isRunning ? (
                <Hourglass className="w-4 h-4 text-emerald-600 flex-shrink-0 animate-pulse" />
              ) : isPaused ? (
                <Pause className="w-4 h-4 text-amber-500 flex-shrink-0" />
              ) : (
                <Circle className={`w-4 h-4 flex-shrink-0 ${priorityColors[task.priority] || "text-slate-400"}`} />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px] py-0">{task.priority}</Badge>
                  <span className="text-[10px] text-slate-400">{format(new Date(task.due_date), "dd.MM")}</span>
                  {isPaused && <span className="text-[10px] font-medium text-amber-600">PAUSED</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isRunning && (
                  <div className="text-right">
                    <LiveTimer startedAt={task.started_at} accumulatedSeconds={accSecs} />
                    {hourlyRate > 0 && (
                      <div className="text-xs"><LiveCost startedAt={task.started_at} hourlyRate={hourlyRate} accumulatedSeconds={accSecs} /></div>
                    )}
                  </div>
                )}
                {isPaused && accSecs > 0 && (
                  <span className="font-mono text-sm text-amber-600">{formatDuration(accSecs)}</span>
                )}
                {isRunning ? (
                  <Button size="sm" className="h-8 bg-slate-700 hover:bg-slate-800 gap-1 text-xs" onClick={() => onOpenAction(task)}>
                    <Square className="w-3 h-3" /> Stop
                  </Button>
                ) : isPaused ? (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-8 gap-1 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => onStartTimer(task)}>
                      <Play className="w-3 h-3" /> Resume
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-600" onClick={() => onOpenAction(task)}>
                      <Square className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => onStartTimer(task)}>
                    <Play className="w-3 h-3" /> Start
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Completed tasks (collapsible) */}
        {showCompleted && completed.length > 0 && (
          <div className="border-t border-slate-100 bg-slate-50/50">
            {completed.map((task) => {
              const duration = (task.started_at && task.completed_at)
                ? formatDuration(Math.floor((new Date(task.completed_at) - new Date(task.started_at)) / 1000))
                : null;
              return (
                <div key={task.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-slate-100 last:border-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="flex-1 text-sm text-slate-400 line-through truncate">{task.title}</span>
                  {duration && <span className="text-xs text-slate-400">{duration}</span>}
                  {task.task_cost != null && <span className="text-xs text-emerald-600 font-medium">€{parseFloat(task.task_cost).toFixed(2)}</span>}
                  {onDelete && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-red-500 hover:bg-red-50" onClick={() => onDelete(task.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PERFORMANCE TAB — Merged ranking + analytics
// ---------------------------------------------------------------------------

function PerformanceTab({ completedTasks, rankingData }) {
  const [mode, setMode] = useState("employee");
  const [expandedKey, setExpandedKey] = useState(null);

  // Task → employee speed rankings
  const taskEmployeeStats = useMemo(() => {
    const map = {};
    completedTasks.forEach((task) => {
      // Use accumulated_seconds if available, fallback to started_at→completed_at
      const hasTiming = task.accumulated_seconds > 0 || (task.started_at && task.completed_at);
      if (!hasTiming) return;
      const empKey = task.assigned_user_id || task.employee_name;
      if (!empKey) return;
      const name = task.assigned_user_display_name || task.employee_name || "Unknown";
      const dur = task.accumulated_seconds > 0
        ? task.accumulated_seconds / 60
        : (new Date(task.completed_at) - new Date(task.started_at)) / 60000;
      if (dur < 0) return;
      if (!map[task.title]) map[task.title] = {};
      if (!map[task.title][empKey]) map[task.title][empKey] = { name, durations: [], tasks: 0 };
      map[task.title][empKey].durations.push(dur);
      map[task.title][empKey].tasks++;
    });
    const result = {};
    Object.entries(map).forEach(([title, employees]) => {
      const sorted = Object.entries(employees)
        .map(([empKey, data]) => ({
          key: empKey,
          name: data.name,
          avgDuration: data.durations.reduce((a, b) => a + b, 0) / data.durations.length,
          tasks: data.tasks,
        }))
        .sort((a, b) => a.avgDuration - b.avgDuration);
      result[title] = sorted.map((emp, i) => ({ ...emp, rank: i + 1 }));
    });
    return result;
  }, [completedTasks]);

  // Employee performance rows
  const employeeRows = useMemo(() => {
    const map = {};
    const freqMap = {};
    completedTasks.forEach((task) => {
      const empKey = task.assigned_user_id || task.employee_name;
      if (!empKey) return;
      const name = task.assigned_user_display_name || task.employee_name || "Unknown";
      if (!map[empKey]) map[empKey] = { key: empKey, name, tasks: 0, totalCost: 0, totalDuration: 0, timedTasks: 0 };
      map[empKey].tasks++;
      if (task.task_cost != null) map[empKey].totalCost += parseFloat(task.task_cost);
      // Use accumulated_seconds if available (more accurate with pause/resume), fallback to started_at→completed_at
      const durSecs = task.accumulated_seconds > 0
        ? task.accumulated_seconds
        : (task.started_at && task.completed_at ? (new Date(task.completed_at) - new Date(task.started_at)) / 1000 : null);
      if (durSecs !== null && durSecs >= 0) {
        map[empKey].totalDuration += durSecs / 60;
        map[empKey].timedTasks++;
      }
      if (!freqMap[empKey]) freqMap[empKey] = {};
      freqMap[empKey][task.title] = (freqMap[empKey][task.title] || 0) + 1;
    });
    return Object.values(map)
      .map((s) => {
        // Build a unified task breakdown: every task they've done, with count + rank if available
        const allTasks = Object.entries(freqMap[s.key] || {}).map(([title, count]) => {
          const ranks = taskEmployeeStats[title];
          const mine = ranks ? ranks.find((e) => e.key === s.key) : null;
          const teamAvg = ranks ? ranks.reduce((a, b) => a + b.avgDuration, 0) / ranks.length : null;
          return {
            title,
            count,
            rank: mine ? mine.rank : null,
            total: ranks ? ranks.length : null,
            avgDuration: mine ? mine.avgDuration : null,
            teamAvgDuration: teamAvg,
          };
        });
        // Sort: ranked tasks first (by rank), then unranked by count
        allTasks.sort((a, b) => {
          if (a.rank !== null && b.rank !== null) return a.rank - b.rank;
          if (a.rank !== null) return -1;
          if (b.rank !== null) return 1;
          return b.count - a.count;
        });
        return {
          ...s,
          avgDuration: s.timedTasks > 0 ? s.totalDuration / s.timedTasks : null,
          allTasks,
        };
      })
      .sort((a, b) => b.tasks - a.tasks);
  }, [completedTasks, taskEmployeeStats]);

  // Task performance rows
  const taskRows = useMemo(
    () =>
      Object.entries(taskEmployeeStats)
        .map(([title, employees]) => ({
          title,
          totalInstances: employees.reduce((s, e) => s + e.tasks, 0),
          employees,
        }))
        .sort((a, b) => b.totalInstances - a.totalInstances),
    [taskEmployeeStats]
  );

  const assigned = completedTasks.filter((t) => t.assigned_user_id || t.employee_name).length;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Performance
              </CardTitle>
              <p className="text-sm text-slate-500 mt-0.5">Based on {assigned} completed assigned tasks</p>
            </div>
            <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-0.5 flex-shrink-0">
              <button
                onClick={() => { setMode("employee"); setExpandedKey(null); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === "employee" ? "bg-white shadow text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
              >
                By Employee
              </button>
              <button
                onClick={() => { setMode("task"); setExpandedKey(null); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === "task" ? "bg-white shadow text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
              >
                By Task
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {mode === "employee" ? (
            employeeRows.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-8">No completed tasks with assigned employees yet.</p>
            ) : (
              <div className="space-y-1">
                {employeeRows.map((emp, idx) => {
                  const isOpen = expandedKey === emp.key;
                  return (
                    <div key={emp.key} className="border border-slate-100 rounded-lg overflow-hidden">
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 select-none"
                        onClick={() => setExpandedKey(isOpen ? null : emp.key)}
                      >
                        <span className="text-sm font-bold text-slate-400 w-6 text-center">#{idx + 1}</span>
                        <span className="font-medium text-slate-800 flex-1">{emp.name}</span>
                        <span className="text-xs text-slate-500">{emp.tasks} tasks</span>
                        {emp.avgDuration !== null && (
                          <span className="text-xs text-slate-500">{formatMinutes(emp.avgDuration)} avg</span>
                        )}
                        <span className="text-xs font-medium text-emerald-600">€{emp.totalCost.toFixed(2)}</span>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                      {isOpen && (
                        <div className="border-t border-slate-100 px-4 py-4 bg-slate-50/60 space-y-4">
                          {emp.allTasks.length > 0 ? (
                            <>
                              {/* Fastest at badges */}
                              {emp.allTasks.filter((t) => t.rank === 1).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fastest at</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {emp.allTasks
                                      .filter((t) => t.rank === 1)
                                      .map((t, i) => {
                                        const pctFaster =
                                          t.total > 1 && t.teamAvgDuration
                                            ? Math.round(((t.teamAvgDuration - t.avgDuration) / t.teamAvgDuration) * 100)
                                            : null;
                                        return (
                                          <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-3 py-1 font-medium">
                                            <Zap className="w-3 h-3 flex-shrink-0" />
                                            {t.title}
                                            <span className="text-emerald-500 font-normal">{formatMinutes(t.avgDuration)}</span>
                                            {pctFaster !== null && pctFaster > 0 && (
                                              <span className="text-emerald-600 font-semibold">+{pctFaster}%</span>
                                            )}
                                          </span>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                              {/* Unified task breakdown table */}
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-400 border-b border-slate-100">
                                    <th className="text-left pb-1.5 pr-4">Task</th>
                                    <th className="text-right pb-1.5 pr-4">Times done</th>
                                    <th className="text-right pb-1.5 pr-4">Avg time</th>
                                    <th className="text-right pb-1.5">Rank</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {emp.allTasks.map((t, i) => (
                                    <tr key={i} className={`border-b border-slate-50 last:border-0 ${t.rank === 1 ? "bg-emerald-50/40" : ""}`}>
                                      <td className="py-1.5 pr-4 text-slate-700 font-medium">{t.title}</td>
                                      <td className="py-1.5 pr-4 text-right">
                                        <span className="inline-flex items-center gap-1 text-slate-600 font-semibold">
                                          {t.count}<span className="text-slate-400 font-normal">×</span>
                                        </span>
                                      </td>
                                      <td className="py-1.5 pr-4 text-right text-slate-600">
                                        {t.avgDuration !== null ? formatMinutes(t.avgDuration) : <span className="text-slate-300">—</span>}
                                      </td>
                                      <td className="py-1.5 text-right">
                                        {t.rank !== null ? (
                                          <>
                                            <RankBadge rank={t.rank} />
                                            <span className="text-slate-400 ml-1">/{t.total}</span>
                                          </>
                                        ) : (
                                          <span className="text-slate-300">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          ) : (
                            <p className="text-xs text-slate-400 text-center py-2">No completed tasks yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : taskRows.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">No timed tasks completed by multiple employees yet.</p>
          ) : (
            <div className="space-y-1">
              {taskRows.map((task) => {
                const isOpen = expandedKey === task.title;
                return (
                  <div key={task.title} className="border border-slate-100 rounded-lg overflow-hidden">
                    <div
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => setExpandedKey(isOpen ? null : task.title)}
                    >
                      <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="font-medium text-slate-800 flex-1">{task.title}</span>
                      <span className="text-xs text-slate-500">
                        {task.employees.length} employee{task.employees.length !== 1 ? "s" : ""}
                      </span>
                      <span className="text-xs text-slate-400">{task.totalInstances} done</span>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                    {isOpen && (
                      <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-400 border-b border-slate-100">
                              <th className="text-left pb-1.5 pr-3">Rank</th>
                              <th className="text-left pb-1.5 pr-4">Employee</th>
                              <th className="text-right pb-1.5 pr-4">Avg time</th>
                              <th className="text-right pb-1.5">Done</th>
                            </tr>
                          </thead>
                          <tbody>
                            {task.employees.map((emp) => (
                              <tr key={emp.key} className="border-b border-slate-50 last:border-0">
                                <td className="py-1.5 pr-3"><RankBadge rank={emp.rank} /></td>
                                <td className="py-1.5 pr-4 text-slate-700 font-medium">{emp.name}</td>
                                <td className="py-1.5 pr-4 text-right text-slate-600">{formatMinutes(emp.avgDuration)}</td>
                                <td className="py-1.5 text-right text-slate-500">{emp.tasks}x</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const emptyForm = () => ({
  title: "",
  category: "Küche",
  due_date: format(new Date(), "yyyy-MM-dd"),
  priority: "Normal",
  recurrence_type: "one_time",
  recurrence_days: [],
});

export default function Tasks() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  const isEmployee = authUser?.role === "staff";

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [allocForm, setAllocForm] = useState({ recipe_id: "", percentage: "" });
  const [pendingAllocations, setPendingAllocations] = useState([]);
  const [activeTab, setActiveTab] = useState(isEmployee ? "team" : "library");
  const [assigningTask, setAssigningTask] = useState(null);
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [assignDueDate, setAssignDueDate] = useState("");

  // Data
  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list("-due_date"),
    initialData: [],
    refetchInterval: 10000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["org-users"],
    queryFn: () => base44.organizations.listUsers(),
    initialData: [],
    enabled: !isEmployee,
  });

  const activeUsers = users.filter((u) => u.is_active !== false);

  const { data: recipes = [] } = useQuery({
    queryKey: ["recipes"],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
    enabled: !isEmployee,
  });

  const activeRecipes = recipes.filter((r) => r.active !== false);

  const { data: allocations = [], refetch: refetchAllocations } = useQuery({
    queryKey: ["task-allocations", editingTask?.id],
    queryFn: () => base44.taskRecipeAllocations.listByTask(editingTask.id),
    initialData: [],
    enabled: !!editingTask?.id,
  });

  const { data: rankingData = [] } = useQuery({
    queryKey: ["tasks-ranking"],
    queryFn: () => base44.tasks.ranking(),
    initialData: [],
    enabled: !isEmployee && activeTab === "performance",
  });

  // Derived data
  const today = format(new Date(), "yyyy-MM-dd");
  const templateTasks = tasks.filter((t) => t.is_template);
  const activeTasks = tasks.filter((t) => !t.is_template && !t.completed);
  const completedTasks = tasks.filter((t) => t.completed && !t.is_template);
  const todayTasks = tasks.filter((t) => t.due_date === today && !t.is_template && !t.completed);

  const templateInstanceMap = useMemo(() => {
    const map = {};
    for (const tpl of templateTasks) {
      const instances = tasks.filter((t) => !t.is_template && t.title === tpl.title);
      map[tpl.id] = {
        active: instances.filter((t) => !t.completed),
        completed: instances.filter((t) => t.completed),
      };
    }
    return map;
  }, [tasks, templateTasks]);

  // Mutations
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["tasks"] });

  const createAllocMutation = useMutation({
    mutationFn: (data) => base44.taskRecipeAllocations.create(data),
    onSuccess: () => { refetchAllocations(); setAllocForm({ recipe_id: "", percentage: "" }); },
    onError: (err) => {
      const detail = err?.response?.data?.non_field_errors?.[0] || err?.response?.data?.detail || err.message;
      alert(detail);
    },
  });

  const deleteAllocMutation = useMutation({
    mutationFn: (id) => base44.taskRecipeAllocations.delete(id),
    onSuccess: () => refetchAllocations(),
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const task = await base44.entities.Task.create({ ...data, is_template: true });
      for (const alloc of pendingAllocations) {
        await base44.taskRecipeAllocations.create({
          task_id: task.id,
          recipe_id: alloc.recipe_id,
          percentage: alloc.percentage,
        });
      }
      return task;
    },
    onSuccess: () => {
      invalidate();
      setIsDialogOpen(false);
      setForm(emptyForm());
      setPendingAllocations([]);
    },
    onError: (err) => alert(err?.response?.data?.detail || err.message),
  });

  const assignMutation = useMutation({
    mutationFn: ({ taskId, userIds, dueDate }) => base44.tasks.assign(taskId, userIds, dueDate),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["tasks-ranking"] });
      setAssigningTask(null);
      setAssignUserIds([]);
      setAssignDueDate("");
      setActiveTab("team");
    },
    onError: (err) => alert(err?.response?.data?.error || err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["tasks-ranking"] });
      setIsDialogOpen(false);
      setEditingTask(null);
      setForm(emptyForm());
    },
    onError: (err) => alert(err?.response?.data?.detail || err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => { invalidate(); setDeleteTaskId(null); },
    onError: (err) => { alert(err?.response?.data?.detail || err.message || "Delete failed"); setDeleteTaskId(null); },
  });

  // Handlers
  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || "",
      category: task.category || "Küche",
      due_date: task.due_date || format(new Date(), "yyyy-MM-dd"),
      priority: task.priority || "Normal",
      recurrence_type: task.recurrence_type || "one_time",
      recurrence_days: Array.isArray(task.recurrence_days) ? task.recurrence_days : [],
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingTask) {
      updateMutation.mutate({ id: editingTask.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const [actionTask, setActionTask] = useState(null);

  const handleStartTimer = (task) => {
    // Start or resume: set started_at, clear paused_at
    updateMutation.mutate({
      id: task.id,
      data: { started_at: new Date().toISOString(), paused_at: null, completed: false },
    });
  };

  const handleCompleteTimer = (task) => {
    const now = new Date();
    const accSecs = task.accumulated_seconds || 0;
    // Add current session seconds to accumulated
    let sessionSecs = 0;
    if (task.started_at) {
      sessionSecs = Math.floor((now.getTime() - new Date(task.started_at).getTime()) / 1000);
    }
    const totalSecs = accSecs + sessionSecs;
    let cost = null;
    const rate = parseFloat(task.assigned_user_hourly_rate) || parseFloat(task.employee_hourly_rate) || 0;
    if (rate > 0 && totalSecs > 0) {
      cost = parseFloat(((totalSecs / 3600) * rate).toFixed(2));
    }
    updateMutation.mutate({
      id: task.id,
      data: {
        completed_at: now.toISOString(),
        completed: true,
        task_cost: cost,
        accumulated_seconds: totalSecs,
        paused_at: null,
      },
    });
  };

  const handlePauseTimer = (task) => {
    const now = new Date();
    const accSecs = task.accumulated_seconds || 0;
    let sessionSecs = 0;
    if (task.started_at) {
      sessionSecs = Math.floor((now.getTime() - new Date(task.started_at).getTime()) / 1000);
    }
    updateMutation.mutate({
      id: task.id,
      data: {
        started_at: null,
        paused_at: now.toISOString(),
        accumulated_seconds: accSecs + sessionSecs,
      },
    });
  };

  const handleResetTimer = (task) => {
    updateMutation.mutate({
      id: task.id,
      data: {
        started_at: null,
        paused_at: null,
        accumulated_seconds: 0,
        completed: false,
        completed_at: null,
        task_cost: null,
      },
    });
  };

  // -----------------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------------

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t("tasks.title")}</h1>
              <p className="text-slate-500 mt-1">{t("tasks.subtitle")}</p>
            </div>
            {!isEmployee && (
              <Button className="bg-emerald-700 hover:bg-emerald-800 shadow-md" onClick={() => { setEditingTask(null); setForm(emptyForm()); setPendingAllocations([]); setIsDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                {t("tasks.createTask")}
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-100 rounded-xl"><Calendar className="w-5 h-5 text-emerald-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">{t("tasks.dueToday")}</p>
                    <p className="text-2xl font-bold text-slate-900">{todayTasks.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-orange-100 rounded-xl"><Circle className="w-5 h-5 text-orange-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">{t("tasks.open")}</p>
                    <p className="text-2xl font-bold text-slate-900">{activeTasks.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-100 rounded-xl"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">{t("tasks.completed")}</p>
                    <p className="text-2xl font-bold text-slate-900">{completedTasks.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Three tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white shadow-sm border border-slate-200">
              {!isEmployee && (
                <TabsTrigger value="library" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800">
                  <BookOpen className="w-3.5 h-3.5" />
                  Library
                  <span className="ml-1 text-xs opacity-60">({templateTasks.length})</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="team" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800">
                <Users className="w-3.5 h-3.5" />
                Team
                {activeTasks.length > 0 && (
                  <span className="ml-1 bg-emerald-700 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none">{activeTasks.length}</span>
                )}
              </TabsTrigger>
              {!isEmployee && (
                <TabsTrigger value="performance" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800">
                  <Trophy className="w-3.5 h-3.5" />
                  Performance
                </TabsTrigger>
              )}
            </TabsList>

            {/* Library */}
            {!isEmployee && (
              <TabsContent value="library">
                <LibraryTab
                  templates={templateTasks}
                  instanceMap={templateInstanceMap}
                  onEdit={handleEdit}
                  onDelete={setDeleteTaskId}
                  onAssign={(t) => { setAssigningTask(t); setAssignDueDate(t.due_date || today); }}
                  onStartTimer={handleStartTimer}
                  onOpenAction={setActionTask}
                />
              </TabsContent>
            )}

            {/* Team */}
            <TabsContent value="team">
              <TeamTab
                tasks={tasks}
                users={activeUsers}
                isEmployee={isEmployee}
                authUser={authUser}
                onStartTimer={handleStartTimer}
                onOpenAction={setActionTask}
                onDelete={!isEmployee ? setDeleteTaskId : null}
              />
            </TabsContent>

            {/* Performance */}
            {!isEmployee && (
              <TabsContent value="performance">
                <PerformanceTab
                  completedTasks={completedTasks}
                  rankingData={rankingData}
                />
              </TabsContent>
            )}
          </Tabs>

          {/* Create/Edit Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) { setEditingTask(null); setForm(emptyForm()); setAllocForm({ recipe_id: "", percentage: "" }); setPendingAllocations([]); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingTask ? t("tasks.editTask") : t("tasks.newTask")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t("tasks.task")}</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("tasks.taskPlaceholder")} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("tasks.category")}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue>{{"Küche": t("tasks.categories.kitchen"), "Pizza": t("tasks.categories.pizza"), "FOH": t("tasks.categories.foh"), "General": t("tasks.categories.general")}[form.category] || form.category}</SelectValue></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Küche">{t("tasks.categories.kitchen")}</SelectItem>
                        <SelectItem value="Pizza">{t("tasks.categories.pizza")}</SelectItem>
                        <SelectItem value="FOH">{t("tasks.categories.foh")}</SelectItem>
                        <SelectItem value="General">{t("tasks.categories.general")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("tasks.priority")}</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue>{{"Niedrig": t("tasks.priorities.low"), "Normal": t("tasks.priorities.normal"), "Hoch": t("tasks.priorities.high"), "Dringend": t("tasks.priorities.urgent")}[form.priority] || form.priority}</SelectValue></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Niedrig">{t("tasks.priorities.low")}</SelectItem>
                        <SelectItem value="Normal">{t("tasks.priorities.normal")}</SelectItem>
                        <SelectItem value="Hoch">{t("tasks.priorities.high")}</SelectItem>
                        <SelectItem value="Dringend">{t("tasks.priorities.urgent")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{t("tasks.dueDate")}</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>{t("tasks.recurrence")}</Label>
                  <Select
                    value={form.recurrence_type}
                    onValueChange={(v) => setForm({ ...form, recurrence_type: v, recurrence_days: [] })}
                  >
                    <SelectTrigger><SelectValue>{t(`tasks.recurrenceTypes.${form.recurrence_type}`)}</SelectValue></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">{t("tasks.recurrenceTypes.one_time")}</SelectItem>
                      <SelectItem value="daily">{t("tasks.recurrenceTypes.daily")}</SelectItem>
                      <SelectItem value="weekly">{t("tasks.recurrenceTypes.weekly")}</SelectItem>
                      <SelectItem value="monthly">{t("tasks.recurrenceTypes.monthly")}</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Weekly day picker */}
                  {form.recurrence_type === "weekly" && (
                    <div className="flex gap-1 pt-1">
                      {[
                        { key: "mon", idx: 0 },
                        { key: "tue", idx: 1 },
                        { key: "wed", idx: 2 },
                        { key: "thu", idx: 3 },
                        { key: "fri", idx: 4 },
                        { key: "sat", idx: 5 },
                        { key: "sun", idx: 6 },
                      ].map(({ key, idx }) => {
                        const active = form.recurrence_days.includes(idx);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              const days = active
                                ? form.recurrence_days.filter((d) => d !== idx)
                                : [...form.recurrence_days, idx].sort((a, b) => a - b);
                              setForm({ ...form, recurrence_days: days });
                            }}
                            className={`flex-1 text-xs py-1 rounded font-medium transition-colors ${
                              active
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {t(`tasks.weekDays.${key}`)}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Monthly day picker */}
                  {form.recurrence_type === "monthly" && (
                    <div className="grid grid-cols-7 gap-1 pt-1">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                        const active = form.recurrence_days.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              const days = active
                                ? form.recurrence_days.filter((d) => d !== day)
                                : [...form.recurrence_days, day].sort((a, b) => a - b);
                              setForm({ ...form, recurrence_days: days });
                            }}
                            className={`text-xs py-1 rounded font-medium transition-colors ${
                              active
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recipe allocations */}
                {(() => {
                  const displayedAllocations = editingTask ? allocations : pendingAllocations;
                  const totalPct = displayedAllocations.reduce((s, a) => s + parseFloat(a.percentage || 0), 0);
                  const handleAddAlloc = () => {
                    if (!allocForm.recipe_id || !allocForm.percentage) return;
                    if (editingTask) {
                      createAllocMutation.mutate({
                        task_id: editingTask.id,
                        recipe_id: parseInt(allocForm.recipe_id, 10),
                        percentage: parseFloat(allocForm.percentage),
                      });
                    } else {
                      const recipe = activeRecipes.find((r) => String(r.id) === allocForm.recipe_id);
                      setPendingAllocations((prev) => [
                        ...prev,
                        { recipe_id: parseInt(allocForm.recipe_id, 10), recipe_name: recipe?.name || "", percentage: parseFloat(allocForm.percentage) },
                      ]);
                      setAllocForm({ recipe_id: "", percentage: "" });
                    }
                  };
                  const handleRemoveAlloc = (a, idx) => {
                    if (editingTask) {
                      deleteAllocMutation.mutate(a.id);
                    } else {
                      setPendingAllocations((prev) => prev.filter((_, i) => i !== idx));
                    }
                  };
                  return (
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-slate-500" />
                        <Label className="text-sm font-semibold text-slate-700">Recipe Allocations</Label>
                        {displayedAllocations.length > 0 && (
                          <span className="ml-auto text-xs text-slate-500">Total: {totalPct.toFixed(0)}%</span>
                        )}
                      </div>
                      {displayedAllocations.length > 0 && (
                        <div className="space-y-1.5">
                          {displayedAllocations.map((a, idx) => (
                            <div key={a.id ?? idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                              <span className="flex-1 text-slate-700 truncate">{a.recipe_name}</span>
                              <span className="font-medium text-emerald-700 w-12 text-right">{parseFloat(a.percentage).toFixed(0)}%</span>
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:bg-red-50" onClick={() => handleRemoveAlloc(a, idx)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <Select value={allocForm.recipe_id} onValueChange={(v) => setAllocForm({ ...allocForm, recipe_id: v })}>
                            <SelectTrigger className="h-8 text-sm truncate"><SelectValue placeholder="Select recipe..." /></SelectTrigger>
                            <SelectContent className="min-w-[200px]">
                              {activeRecipes.map((r) => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-20">
                          <Input type="number" min="1" max="100" step="1" className="h-8 text-sm" placeholder="%" value={allocForm.percentage} onChange={(e) => setAllocForm({ ...allocForm, percentage: e.target.value })} />
                        </div>
                        <Button type="button" size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" disabled={!allocForm.recipe_id || !allocForm.percentage || createAllocMutation.isPending} onClick={handleAddAlloc}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setEditingTask(null); setForm(emptyForm()); setAllocForm({ recipe_id: "", percentage: "" }); setPendingAllocations([]); }}>{t("tasks.cancel")}</Button>
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">{editingTask ? t("tasks.update") : t("tasks.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Assign Dialog */}
          <Dialog open={!!assigningTask} onOpenChange={(open) => { if (!open) { setAssigningTask(null); setAssignUserIds([]); setAssignDueDate(""); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign: {assigningTask?.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("tasks.dueDate")}</Label>
                  <Input type="date" className="mt-1" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)} />
                </div>
                <div>
                  <Label>Select employees</Label>
                  <div className="border border-slate-200 rounded-lg mt-1 max-h-48 overflow-y-auto">
                    {activeUsers.length === 0 ? (
                      <p className="text-xs text-slate-400 px-3 py-2">No users available</p>
                    ) : (
                      activeUsers.map((u) => {
                        const displayName = `${u.first_name} ${u.last_name}`.trim() || u.username;
                        const rate = parseFloat(u.hourly_rate || 0);
                        const checked = assignUserIds.includes(u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                            <Checkbox checked={checked} onCheckedChange={(c) => setAssignUserIds((prev) => c ? [...prev, u.id] : prev.filter((id) => id !== u.id))} />
                            <span className="text-sm text-slate-700 flex-1">{displayName}</span>
                            {rate > 0 && <span className="text-xs text-slate-400">€{rate}/h</span>}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {assignUserIds.length > 0 && (
                    <p className="text-xs text-emerald-600 mt-1.5">
                      {assignUserIds.length} employee{assignUserIds.length > 1 ? "s" : ""} selected
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAssigningTask(null)}>{t("tasks.cancel")}</Button>
                  <Button
                    className="bg-emerald-700 hover:bg-emerald-800"
                    disabled={assignUserIds.length === 0 || assignMutation.isPending}
                    onClick={() => assignMutation.mutate({ taskId: assigningTask.id, userIds: assignUserIds, dueDate: assignDueDate })}
                  >
                    Assign
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Task Action Modal (Complete / Pause / Reset) */}
          <TaskActionModal
            task={actionTask}
            open={!!actionTask}
            onClose={() => setActionTask(null)}
            onComplete={handleCompleteTimer}
            onPause={handlePauseTimer}
            onReset={handleResetTimer}
          />

          {/* Delete Confirmation */}
          <AlertDialog open={!!deleteTaskId} onOpenChange={() => setDeleteTaskId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("tasks.deleteTask")}</AlertDialogTitle>
                <AlertDialogDescription>{t("tasks.deleteTaskDesc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("tasks.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMutation.mutate(deleteTaskId)} className="bg-red-600 hover:bg-red-700">
                  {t("tasks.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>
      </div>
    </Layout>
  );
}
